import React from "react";
import {
  Coffee,
  HeartHandshake,
  Tv,
  UtensilsCrossed,
  Users,
  Wifi,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

export const SUITE_IMAGES = {
  suiteOrangeWide:
    "https://vibe.filesafe.space/1787327131284421342/attachments/65bd3a1f-5ba1-43da-8b0f-00417c5d48ed.png",
  suiteBlueTv:
    "https://vibe.filesafe.space/1787327131284421342/attachments/8aa7efff-0495-4d82-87ce-34d1f349e573.png",
  suiteOrangeRecliner:
    "https://vibe.filesafe.space/1787327131284421342/attachments/6222b5e9-ba0f-42b4-a0c1-50b2512c1da5.png",
  suiteOrangeFull:
    "https://vibe.filesafe.space/1787327131284421342/attachments/7ab7518d-d9c8-4417-b2f2-05299d3b1f3b.png",
  suiteBlueDesk:
    "https://vibe.filesafe.space/1787327131284421342/attachments/bb78020b-18d0-4701-b491-b8867ddad417.png",
};

export const SaintPrivateSuites: React.FC = () => {
  return (
    <section className="py-16 sm:py-24 bg-card border-y border-border">
      <div className="container-px mx-auto max-w-6xl px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5 text-xs font-semibold text-accent uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" />
            Dedicated SAINT® Lounge Wing &amp; Private Suites
          </span>
          <h2 className="mt-4 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl leading-tight">
            Designed for Comfort, Privacy &amp; Family: Your Outpatient Retreat
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Because SAINT® is delivered across five consecutive full outpatient
            days, your environment matters just as much as your clinical care.
            Relax in our dedicated SAINT Lounge Wing—complete with private
            furnished suites, dedicated on-site care team check-ins, personal
            workstations, and spaces designed so you and your loved ones feel
            completely at home.
          </p>
        </div>

        {/* Feature Highlights Grid */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="p-5 rounded-xl border border-border bg-background shadow-soft flex flex-col">
            <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
              <HeartHandshake className="h-5 w-5" />
            </div>
            <h3 className="font-headings text-base font-bold text-primary">
              On-Site Care Team
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed flex-1">
              Our clinical and hospitality team is with you every step of the
              day, checking in between sessions to ensure you are comfortable,
              supported, and relaxed.
            </p>
          </div>

          <div className="p-5 rounded-xl border border-border bg-background shadow-soft flex flex-col">
            <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
              <UtensilsCrossed className="h-5 w-5" />
            </div>
            <h3 className="font-headings text-base font-bold text-primary">
              Lunch &amp; Refreshment Station
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed flex-1">
              Enjoy your lunch in your private suite during the 50-minute break.
              Suites feature Keurig coffee, cold drinks, healthy snacks, and
              mini-fridges.
            </p>
          </div>

          <div className="p-5 rounded-xl border border-border bg-background shadow-soft flex flex-col">
            <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
              <Users className="h-5 w-5" />
            </div>
            <h3 className="font-headings text-base font-bold text-primary">
              Bring Loved Ones Along
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed flex-1">
              Spouses, family members, or caregivers are welcome. Large
              sectionals and comfortable recliners ensure your support team has
              their own quiet sanctuary.
            </p>
          </div>

          <div className="p-5 rounded-xl border border-border bg-background shadow-soft flex flex-col">
            <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
              <Tv className="h-5 w-5" />
            </div>
            <h3 className="font-headings text-base font-bold text-primary">
              Work, Rest, or Stream
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed flex-1">
              Private desks, high-speed Wi-Fi, large smart 4K TVs, and leather
              power recliners let you stay productive or unwind peacefully
              between treatments.
            </p>
          </div>
        </div>

        {/* Visual Gallery */}
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Photo 1 */}
          <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-soft group">
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
              <img
                src={SUITE_IMAGES.suiteBlueTv}
                alt="Private SAINT® lounge suite with blue sectional, power recliner, refreshment fridge, coffee station, and flat-screen TV"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="p-5">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                Private Suite Experience
              </span>
              <h4 className="mt-1 font-headings text-base font-bold text-primary">
                Executive Blue Lounge Suite
              </h4>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                Featuring a plush sectional, power recliner, stocked beverage
                fridge, coffee bar, and 4K entertainment between your 10-minute
                sessions.
              </p>
            </div>
          </div>

          {/* Photo 2 */}
          <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-soft group">
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
              <img
                src={SUITE_IMAGES.suiteOrangeFull}
                alt="Dedicated private SAINT® suite with mid-century orange sofa, executive work desk, and full kitchen access"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="p-5">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                Work &amp; Dining Comfort
              </span>
              <h4 className="mt-1 font-headings text-base font-bold text-primary">
                Coral Springs Sun Lounge Suite
              </h4>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                Quiet desk setup for remote work, comfortable seating for
                companions, and immediate access to full kitchen facilities for
                lunch.
              </p>
            </div>
          </div>

          {/* Photo 3 */}
          <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-soft group md:col-span-2 lg:col-span-1">
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
              <img
                src={SUITE_IMAGES.suiteBlueDesk}
                alt="Quiet private suite with desk, power leather recliner, and blue sectional for patient rest during 5-day SAINT week"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="p-5">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                Rest &amp; Recharging
              </span>
              <h4 className="mt-1 font-headings text-base font-bold text-primary">
                Quiet Rest &amp; Focus Nook
              </h4>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                Designed so you never feel like a clinical number. Enjoy 50
                minutes of calm, uninterrupted peace between each targeted
                treatment.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Trust Callout */}
        <div className="mt-10 rounded-2xl border border-accent/20 bg-accent/5 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 justify-between">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-accent text-accent-foreground flex items-center justify-center shrink-0">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-headings text-lg font-bold text-primary">
                A True Concierge Outpatient Mental Health Experience
              </h4>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                Whether traveling from across Florida, another state, or
                internationally, our on-site team ensures your five-day SAINT®
                week is seamless, comfortable, and discreet.
              </p>
            </div>
          </div>
          <a
            href="#consultation"
            className="inline-flex shrink-0 items-center justify-center rounded-xl bg-gradient-accent px-6 py-3.5 text-sm font-bold text-accent-foreground shadow-glow transition-opacity hover:opacity-90"
          >
            Tour Our Suites at Consultation
          </a>
        </div>
      </div>
    </section>
  );
};
